import { useMemo } from 'react';
import { Checkbox, DateInput, NumberInput, Text, Contact, SearchSelect, Toggle } from './FieldComponents';
import { getBillingEntryFlagEditorValue } from './billingEntryMutation';

const EMPTY_LIST = [];

export const CreateFlatFee = ({
    entryData,
    setEntryData,
    users = EMPTY_LIST,
    flatFeeOptions = EMPTY_LIST,
    flatFeeOptions2 = EMPTY_LIST,
    entryKind = 'flat_fee',
    showTeamMember = false,
    embedded = false,
}) => {
    const isExpense = entryKind === 'expense' || entryData.entry_kind === 'expense' || entryData.item_type === 'expense';
    const safeUsers = Array.isArray(users) ? users : EMPTY_LIST;
    const effectiveUserId = entryData.user_id ?? entryData.effective_user_id ?? entryData.billing_rate_user_id;
    const selectedUser = useMemo(() => {
        if (!effectiveUserId) return null;
        return safeUsers.find(user => Number(user?.id) === Number(effectiveUserId))
            || (entryData.user_name ? { id: effectiveUserId, name: entryData.user_name } : null);
    }, [effectiveUserId, entryData.user_name, safeUsers]);
    const draftValue = getBillingEntryFlagEditorValue(entryData.draft, isExpense);
    const billableValue = getBillingEntryFlagEditorValue(entryData.billable, false);
    const chargeableValue = getBillingEntryFlagEditorValue(entryData.chargeable, false);
    const nullableNumberValue = (value) => value === '' ? null : Number(value);

    return (
        <div className={embedded ? 'modal-content-section billing-entry-editor-content' : 'modal-content-wrapper'}>
            {showTeamMember && (
                <div className='form-group mid team-member'>
                    <label className='subtext'>Team Member</label>
                    <Contact
                        selectedContact={selectedUser}
                        setSelectedContact={(nextUser) => {
                            const nextUserId = nextUser && typeof nextUser === 'object' ? nextUser.id : nextUser;
                            const normalizedUserId = Number(nextUserId) > 0 ? Number(nextUserId) : null;
                            setEntryData(previous => ({
                                ...previous,
                                user_id: normalizedUserId,
                                effective_user_id: normalizedUserId,
                                ...(!normalizedUserId ? { billing_rate_id: null, billing_rate_user_id: null } : {}),
                            }));
                        }}
                        users={safeUsers}
                    />
                </div>
            )}
            <div className='entry-container'>
                <div className='form-group nm'>
                    <label className='subtext'>Date</label>
                    <DateInput
                        value={entryData.date ?? ''}
                        onChange={(val) => setEntryData({ ...entryData, date: val })}
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>{isExpense ? 'Quantity' : 'Quantity*'}</label>
                    <NumberInput
                        type='number'
                        value={entryData.hours ?? entryData.quantity ?? ''}
                        onChange={(val) => setEntryData({
                            ...entryData,
                            hours: nullableNumberValue(val),
                            raw_quantity: nullableNumberValue(val),
                            quantity: nullableNumberValue(val),
                        })}
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>{isExpense ? 'Amount' : 'Price per Unit*'}</label>
                    <NumberInput
                        type='currency'
                        value={entryData.rate ?? ''}
                        onChange={(val) => setEntryData({ ...entryData, rate: nullableNumberValue(val) })}
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Unit</label>
                    <input
                        type='text'
                        placeholder='Unit'
                        value={entryData.unit || ''}
                        onChange={(e) => setEntryData({ ...entryData, unit: e.target.value })}
                        className='text-input'
                    />
                </div>
            </div>

            {isExpense && entryData.payee_name && (
                <div className='form-group mid'>
                    <label className='subtext'>Payee</label>
                    <input
                        type='text'
                        value={entryData.payee_name || ''}
                        onChange={(e) => setEntryData({ ...entryData, payee_name: e.target.value })}
                        className='text-input'
                    />
                </div>
            )}

            <div className='form-group mid'>
                <label className='subtext'>Description*</label>
                <Text
                    type='textarea'
                    value={entryData.description ?? ''}
                    onChange={(val) => setEntryData({ ...entryData, description: val })}
                    placeholder='Description...'
                />
            </div>

            {!isExpense && <div className='form-group nm mid'>
                <label className='subtext'>Flat Fee Code</label>
                <SearchSelect
                    options={Object.fromEntries(flatFeeOptions.map(option => [option.id, option.code]))}
                    value={entryData.flat_fee_code || ''}
                    onChange={(val) => setEntryData({ ...entryData, flat_fee_code: val })}
                    placeholder='Type a Code...'
                />
            </div>}

            {!isExpense && entryData.flat_fee_code && <div className='form-group mid'>
                <label className='subtext'>Flat Fee Code 2</label>
                <SearchSelect
                    options={flatFeeOptions2}
                    value={entryData.flat_fee_code_2 || ''}
                    onChange={(val) => setEntryData({ ...entryData, flat_fee_code_2: val })}
                    placeholder='Type a Code...'
                />
            </div>}

            <Checkbox
                value={draftValue}
                onChange={() => setEntryData(previous => ({ ...previous, draft: !draftValue }))}
                label='Save as Draft'
                hint='Draft entries are not available for a new invoice.'
                space
            />

            <div className='entry-lower-container'>
                <Toggle
                    label='Billable'
                    value={billableValue}
                    onChange={() => setEntryData(prev => ({ ...prev, billable: !billableValue }))}
                />
                <Toggle
                    label='Chargeable'
                    value={chargeableValue}
                    onChange={() => setEntryData(prev => ({ ...prev, chargeable: !chargeableValue }))}
                />
            </div>

        </div>
    );
};

export default CreateFlatFee;
